import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Search, Users, UserPlus, Trash2, Pencil, Download, Filter, X } from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VERIFICATION_STATUS = [
  { value: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-700" },
  { value: "verified", label: "Verified", color: "bg-green-100 text-green-700" },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-700" }
];

export default function RegisteredUsersPage() {
  const { getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    verification_status: "pending"
  });

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, statusFilter, dateFrom, dateTo]);

  const fetchUsers = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.verification_status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await axios.get(`${API_URL}/api/registered-users`, {
        headers: getAuthHeader(),
        params
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch registered users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        await axios.put(`${API_URL}/api/registered-users/${editingUser.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("User updated successfully");
      } else {
        // Create new user
        await axios.post(`${API_URL}/api/registered-users`, formData, {
          headers: getAuthHeader()
        });
        toast.success("User created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API_URL}/api/registered-users/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      email: "",
      verification_status: "pending"
    });
    setEditingUser(null);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      phone: user.phone,
      email: user.email || "",
      verification_status: user.verification_status
    });
    setDialogOpen(true);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(236, 26, 35);
    doc.text("Registered Users Report", 14, 22);
    
    // Metadata
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const generatedDate = new Date().toLocaleString();
    doc.text(`Generated: ${generatedDate}`, 14, 30);
    doc.text(`Total Records: ${users.length}`, 14, 36);
    
    // Filters applied
    if (searchQuery || statusFilter || dateFrom || dateTo) {
      doc.text("Filters Applied:", 14, 42);
      let yPos = 48;
      if (searchQuery) {
        doc.text(`  Search: ${searchQuery}`, 14, yPos);
        yPos += 6;
      }
      if (statusFilter) {
        doc.text(`  Status: ${statusFilter}`, 14, yPos);
        yPos += 6;
      }
      if (dateFrom) {
        doc.text(`  From: ${dateFrom}`, 14, yPos);
        yPos += 6;
      }
      if (dateTo) {
        doc.text(`  To: ${dateTo}`, 14, yPos);
        yPos += 6;
      }
    }
    
    // Table
    const tableData = users.map(user => [
      user.name,
      user.phone,
      user.email || "N/A",
      user.verification_status,
      user.registration_date
    ]);
    
    doc.autoTable({
      startY: (searchQuery || statusFilter || dateFrom || dateTo) ? 60 : 45,
      head: [["Name", "Phone", "Email", "Status", "Registration Date"]],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [236, 26, 35],
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 9,
        cellPadding: 3
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      }
    });
    
    // Save
    const filename = `registered_users_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    toast.success("PDF exported successfully");
  };

  const getStatusBadge = (status) => {
    const statusObj = VERIFICATION_STATUS.find(s => s.value === status);
    return statusObj ? (
      <Badge className={statusObj.color}>{statusObj.label}</Badge>
    ) : (
      <Badge>{status}</Badge>
    );
  };

  const hasActiveFilters = searchQuery || statusFilter || dateFrom || dateTo;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="registered-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Registered Users</h1>
          <p className="text-muted-foreground mt-1">Manage customer registrations and verification</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="rounded-none"
            onClick={exportToPDF}
            disabled={users.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-gradient rounded-none" data-testid="add-user-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">
                  {editingUser ? "Edit Registered User" : "Add New Registered User"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="rounded-none"
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="rounded-none"
                    required
                    data-testid="user-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="rounded-none"
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Verification Status</Label>
                  <Select
                    value={formData.verification_status}
                    onValueChange={(value) => setFormData({ ...formData, verification_status: value })}
                  >
                    <SelectTrigger className="rounded-none" data-testid="user-status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {VERIFICATION_STATUS.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-gradient rounded-none" data-testid="user-submit-btn">
                    {editingUser ? "Update" : "Create"} User
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border rounded-none">
        <CardContent className="p-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Filter className="h-4 w-4" />
                Filters
              </div>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-8 text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, phone, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-none"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="rounded-none">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">All statuses</SelectItem>
                  {VERIFICATION_STATUS.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                placeholder="From date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-none"
                data-testid="date-from-input"
              />
              <Input
                type="date"
                placeholder="To date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-none"
                data-testid="date-to-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border rounded-none">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Total Users</div>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>
        <Card className="border-border rounded-none">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Verified</div>
            <div className="text-2xl font-bold text-green-600">
              {users.filter(u => u.verification_status === "verified").length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border rounded-none">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Pending</div>
            <div className="text-2xl font-bold text-yellow-600">
              {users.filter(u => u.verification_status === "pending").length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-border rounded-none">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Rejected</div>
            <div className="text-2xl font-bold text-red-600">
              {users.filter(u => u.verification_status === "rejected").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-border rounded-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-pulse text-muted-foreground">Loading users...</div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Users className="h-12 w-12 mb-3 opacity-50" />
              <p>No registered users found</p>
              {hasActiveFilters && (
                <Button
                  variant="link"
                  onClick={clearFilters}
                  className="mt-2"
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="font-semibold">Name</TableHead>
                  <TableHead className="font-semibold">Phone</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Verification Status</TableHead>
                  <TableHead className="font-semibold">Policies</TableHead>
                  <TableHead className="font-semibold">Marketing opt-in</TableHead>
                  <TableHead className="font-semibold">Channels</TableHead>
                  <TableHead className="font-semibold">Registration Date</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="table-row" data-testid={`user-row-${user.id}`}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell className="text-muted-foreground">{user.email || "-"}</TableCell>
                    <TableCell>{getStatusBadge(user.verification_status)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {[
                        user.terms_accepted ? "T&C" : null,
                        user.privacy_accepted ? "Privacy" : null,
                        user.cookies_accepted ? "Cookies" : null,
                      ].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          user.marketing_opt_in_status === "confirmed" || user.opt_in_accepted
                            ? "bg-green-100 text-green-700"
                            : user.marketing_opt_in_status === "pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-gray-100 text-gray-600"
                        }
                      >
                        {user.marketing_opt_in_status || (user.opt_in_accepted ? "confirmed" : "none")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {[
                        user.marketing_email ? "Email" : null,
                        user.marketing_sms ? "SMS" : null,
                        user.marketing_whatsapp ? "WhatsApp" : null,
                      ].filter(Boolean).join(" · ") || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(user.registration_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(user)}
                          title="Edit User"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteUser(user.id)}
                          className="text-destructive hover:text-destructive"
                          title="Delete User"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
